import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import { HashLink as Link, NavHashLink as NavLink } from 'react-router-hash-link';
import { CSSTransitionGroup } from 'react-transition-group';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import BackButton from 'components/BackButton';
import Account from 'components/Nav/Account';
import Dropdown from 'components/Nav/Dropdown';
import SideMenu from 'components/Nav/SideMenu';

import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';

import { useSearch } from 'hooks/useSearch';
import { useSettings } from 'hooks/useSettings';

import { storageKeys } from 'settings';

const resetScrollPosition = () => window.scrollTo(0, 0);

const ModalButton = ({ loggedIn, icon = null, onClick, fill = false }) => (
  <li className={clsx({ disabled: loggedIn })}>
    <button type="button" className={`nav__option nav__option--button ${icon}`} onClick={onClick}>
      {!fill && (
        <FontAwesomeIcon
          icon={[typeof icon === 'string' ? 'far' : icon[0], typeof icon === 'string' ? icon : icon[1]]}
        />
      )}
      {fill && (
        <FontAwesomeIcon
          icon={[typeof icon === 'string' ? 'fas' : icon[0], typeof icon === 'string' ? icon : icon[1]]}
        />
      )}
    </button>
  </li>
);

ModalButton.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  onClick: PropTypes.func.isRequired,
  fill: PropTypes.bool,
};

// Creates standardized buttons for the nav bar, based on the navButtons object above.
const NavButton = ({ loggedIn, icon = ['fas', 'stroopwafel'], route }) => (
  <li className={clsx({ disabled: loggedIn })}>
    <NavLink onClick={resetScrollPosition} activeClassName="is-active" to={`${route}`} className={icon}>
      {icon === 'heart' && (
        <FontAwesomeIcon
          icon={[typeof icon === 'string' ? 'far' : icon[0], typeof icon === 'string' ? icon : icon[1]]}
        />
      )}
      {icon !== 'heart' && (
        <FontAwesomeIcon
          icon={[typeof icon === 'string' ? 'fas' : icon[0], typeof icon === 'string' ? icon : icon[1]]}
        />
      )}
    </NavLink>
  </li>
);

// refactor Nav to functional component
const Nav = ({ pathname = '', hasBackButton = true }) => {
  const [loggedIn, setLoggedIn] = useState(false);
  const [firstName, setFirstName] = useState('Login');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [menuToggle, setMenuToggle] = useState(false);
  const [accountToggle, setAccountToggle] = useState(false);
  const [lastCheckedMessages, setLastCheckedMessages] = useState('');
  const [unreadMessageCount, setUnreadMessageCount] = useState('');
  const [show, setShow] = useState(true);
  const [, setLanguage] = useState('en');
  const [trainingComplete, setTrainingComplete] = useState(false);
  const [frame] = useState('');

  const { showSearch } = useSearch();

  const navButtons = {
    favorites: 'heart',
  };

  const modalButtons = {
    search: { icon: 'magnifying-glass', onClick: () => showSearch() },
  };

  const {
    appInfo: { logoWhite },
  } = useSettings();

  const checkForUnreadMessages = useCallback(async () => {
    const res = await DataHandler.requestWithId(APIHandler.getUnreadMessages, lastCheckedMessages);
    if (res?.missed_items?.[0]) {
      setUnreadMessageCount(res.missed_items[0].count);
    }
  }, [lastCheckedMessages]);

  const getLastCheckedMessages = useCallback(async () => {
    const res = await StorageHandler.load(storageKeys.lastCheckedMessage);
    if (res) {
      setLastCheckedMessages(res);
    }
    checkForUnreadMessages();
  }, [checkForUnreadMessages]);

  const getUser = async () => {
    const res = await StorageHandler.load(storageKeys.user);

    if (!res) return;

    setLoggedIn(true);
    setFirstName(res.first_name);
    setLastName(res.last_name);
    setEmail(res.email);

    const progress = await DataHandler.requestWithId(APIHandler.getUserProgress, res.email);

    if (progress.data) {
      const { chapters } = progress.data[0];
      setTrainingComplete(chapters.every((chapter) => chapter.progress >= chapter.total_pages));
    }
  };

  const getLanguage = async () => {
    const lang = await StorageHandler.load(storageKeys.lang);
    setLanguage(lang.langFile);
  };

  const handleAccountToggle = () => {
    setAccountToggle(() => !accountToggle);
    checkForUnreadMessages();
  };

  const handleClickOutside = () => {
    const container = document.getElementById('container');
    setMenuToggle(false);
    setAccountToggle(false);
    container.classList.remove('offsetContainer');
  };

  const handleScroll = () => {
    if (window.scrollY < 15) setShow(true);
    else setShow(false);
  };

  useEffect(() => {
    getLastCheckedMessages();
    getUser();
    getLanguage();

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [getLastCheckedMessages]);

  return (
    <div>
      <nav className="mini">
        <div className="container__backButton">
          {hasBackButton && <BackButton useHistory={false} text={i18n.translate('general.back')} />}
        </div>
        <Link to="/main" className="logo">
          {pathname !== '/main' && pathname !== '/' && pathname !== '/main/' && show && (
            <img src={logoWhite} className="logo" alt="logo" id="logoHeader" />
          )}
        </Link>
        <ul className="secondaryNav">
          {Object.keys(navButtons).map((key) => (
            <NavButton key={key} icon={navButtons[key]} route={`/main/${key}`} loggedIn={loggedIn} />
          ))}
          {Object.keys(modalButtons).map((key) => (
            <ModalButton
              key={key}
              icon={modalButtons[key].icon}
              onClick={modalButtons[key].onClick}
              loggedIn={loggedIn}
            />
          ))}
          <li className={loggedIn ? ' ' : 'navLogin'}>
            {loggedIn ? (
              <Account onClick={handleAccountToggle} firstName={firstName} expanded={accountToggle} />
            ) : (
              <NavLink exact to="/login">
                <button type="button" className="account">
                  <FontAwesomeIcon icon={['fas', 'user']} />
                  {firstName}
                </button>
              </NavLink>
            )}
          </li>
        </ul>
      </nav>

      <CSSTransitionGroup transitionName="accountToggle" transitionEnterTimeout={150} transitionLeaveTimeout={150}>
        {accountToggle && (
          <Dropdown
            handleClickOutside={handleClickOutside}
            outsideClickIgnoreClass="account"
            firstName={firstName}
            lastName={lastName}
            email={email}
            unreadMessageCount={unreadMessageCount}
            trainingComplete={trainingComplete}
            diploma={(src) => frame(src)}
          />
        )}
      </CSSTransitionGroup>
      <CSSTransitionGroup transitionName="menuToggle" transitionEnterTimeout={250} transitionLeaveTimeout={250}>
        {menuToggle && (
          <SideMenu
            handleClickOutside={handleClickOutside}
            outsideClickIgnoreClass="burgerMenu"
            firstName={firstName}
            lastName={lastName}
            email={email}
          />
        )}
      </CSSTransitionGroup>
    </div>
  );
};

export default Nav;

Nav.propTypes = {
  pathname: PropTypes.string,
  hasBackButton: PropTypes.bool,
};

NavButton.propTypes = {
  loggedIn: PropTypes.bool.isRequired,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  route: PropTypes.string.isRequired,
};
